import { XCircle } from 'lucide-react';
import { useState } from 'react';
import { priceTableApi } from '@/api/commercial/price-tables';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';

interface CancelPriceTableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  priceTableId?: string;
  onSuccess?: () => void;
}

export function CancelPriceTableDialog({
  open,
  onOpenChange,
  priceTableId,
  onSuccess,
}: CancelPriceTableDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleCancel = async () => {
    if (!priceTableId) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'ID da tabela não encontrado.',
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await priceTableApi.cancel(priceTableId);

      if (result.success) {
        toast({
          title: 'Sucesso',
          description: 'Tabela cancelada com sucesso!',
        });
        onOpenChange(false);
        onSuccess?.();
        window.location.reload();
      } else {
        toast({
          variant: 'destructive',
          title: 'Erro',
          description: result.error?.global || 'Erro ao cancelar tabela. Tente novamente.',
        });
      }
    } catch (_error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao cancelar tabela. Tente novamente.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-500" />
            Cancelar tabela
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            Ao confirmar esta ação, não será mais possível utilizar a tabela em questão.
            Posteriormente poderá ser clonada apenas.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Manter tabela
          </Button>
          <Button variant="destructive" onClick={handleCancel} disabled={isLoading}>
            {isLoading ? 'Cancelando...' : 'Cancelar tabela'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
